import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import HoursService from '../../service/HoursService';
import { ArrowLeft, Save, Clock, Calendar, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, Alert, Spinner } from 'react-bootstrap';
import { redondearABloque15, minutosATexto } from '../../utils/hoursUtils';

export default function SubmitHours() {
    const navigate = useNavigate();
    const [horas, setHoras] = useState('');
    const [minutos, setMinutos] = useState('');
    const [fechaIngreso, setFechaIngreso] = useState('');
    const [comentarios, setComentarios] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess(false);

        if (!fechaIngreso) {
            setError('Por favor selecciona una fecha.');
            setLoading(false);
            return;
        }

        const horasNum = parseInt(horas, 10);
        const minutosNum = parseInt(minutos, 10);

        if (isNaN(horasNum) || horasNum < 0 || horasNum > 5) {
            setError('Las horas deben estar entre 0 y 5.');
            setLoading(false);
            return;
        }
        if (isNaN(minutosNum) || minutosNum < 0 || minutosNum > 59) {
            setError('Los minutos deben estar entre 0 y 59.');
            setLoading(false);
            return;
        }
        if (horasNum === 0 && minutosNum === 0) {
            setError('Ingresa al menos alguna hora o minuto.');
            setLoading(false);
            return;
        }

        const fechaFormatted = fechaIngreso.split('T')[0];

        const requestData = {
            horas: horasNum,
            minutos: minutosNum,
            fechaIngreso: fechaFormatted,
            comentarios: comentarios.trim() || null
        };

        try {
            await HoursService.submitHours(requestData);
            setSuccess(true);

            setTimeout(() => {
                navigate('/');
            }, 1500);
        } catch (err) {
            console.error('Error submitting hours:', err);

            let msg = 'Error al registrar las horas. Por favor intente nuevamente.';

            if (err.response) {
                const errorData = err.response.data;

                if (typeof errorData === 'string') msg = errorData;
                else if (errorData.message) msg = errorData.message;
                else if (errorData.title) msg = errorData.title;
                else if (errorData.errors) {
                    const first = Object.values(errorData.errors)[0];
                    msg = Array.isArray(first) ? first[0] : first;
                }
            }

            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];

    // Previsión de minutos calculados (redondeo a bloques de 15, mismo criterio que backend)
    const previsionMinutos = useMemo(() => {
        const h = parseInt(horas, 10) || 0;
        const m = parseInt(minutos, 10) || 0;
        if (isNaN(h) || isNaN(m)) return null;
        const total = h * 60 + m;
        if (total <= 0) return null;
        return redondearABloque15(total);
    }, [horas, minutos]);

    return (
        <div className="container py-3 py-md-4">
            <div className="mb-3 mb-md-4">
                <Link to="/" className="text-decoration-none d-flex align-items-center text-muted">
                    <ArrowLeft size={16} className="me-1" /> Volver al inicio
                </Link>
            </div>

            <div className="row justify-content-center">
                <div className="col-12 col-md-8 col-lg-6">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-3 p-md-4">
                            <div className="d-flex align-items-center mb-3 mb-md-4">
                                <div className="bg-primary bg-opacity-10 p-2 p-md-3 rounded-circle me-2 me-md-3 flex-shrink-0">
                                    <Clock className="text-primary" size={20} />
                                </div>
                                <div className="flex-grow-1">
                                    <h2 className="mb-0 fw-bold" style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)' }}>
                                        Registrar Horas Extra
                                    </h2>
                                    <small className="text-muted d-none d-sm-block">Completa el formulario para registrar tus horas</small>
                                </div>
                            </div>

                            {success && (
                                <Alert variant="success" className="mb-4">
                                    <CheckCircle size={18} className="me-2" />
                                    Horas registradas exitosamente. Redirigiendo...
                                </Alert>
                            )}

                            {error && (
                                <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                                    <AlertCircle size={18} className="me-2" />
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="mb-3 mb-md-4">
                                    <label htmlFor="fecha" className="form-label fw-medium d-flex align-items-center gap-2 mb-2">
                                        <Calendar size={18} />
                                        <span>Fecha</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="fecha"
                                        className="form-control"
                                        value={fechaIngreso}
                                        max={today}
                                        onChange={(e) => setFechaIngreso(e.target.value)}
                                        required
                                    />
                                    <small className="text-muted d-block mt-1">Selecciona la fecha en que trabajaste las horas extra</small>
                                </div>

                                <div className="row g-3 mb-3 mb-md-4">
                                    <div className="col-6">
                                        <label htmlFor="horas" className="form-label fw-medium d-flex align-items-center gap-2 mb-2">
                                            <Clock size={18} />
                                            <span>Horas</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="horas"
                                            className="form-control"
                                            min={0}
                                            max={5}
                                            placeholder="0"
                                            value={horas}
                                            onChange={(e) => setHoras(e.target.value)}
                                            required
                                        />
                                        <small className="text-muted d-block mt-1">Entre 0 y 5</small>
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="minutos" className="form-label fw-medium d-flex align-items-center gap-2 mb-2">
                                            <Clock size={18} />
                                            <span>Minutos</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="minutos"
                                            className="form-control"
                                            min={0}
                                            max={59}
                                            placeholder="0"
                                            value={minutos}
                                            onChange={(e) => setMinutos(e.target.value)}
                                            required
                                        />
                                        <small className="text-muted d-block mt-1">Entre 0 y 59</small>
                                    </div>
                                </div>
                                {previsionMinutos != null && (
                                    <div className="mb-3 mb-md-4 p-2 rounded bg-light border border-opacity-25">
                                        <small className="text-muted d-block mb-1">Previsión (redondeo a bloques de 15 min)</small>
                                        <strong>Se calcularán: {previsionMinutos} min</strong>
                                        <span className="text-muted ms-2">({minutosATexto(previsionMinutos)})</span>
                                    </div>
                                )}
                                <div className="mb-3 mb-md-4">
                                    <label htmlFor="comentarios" className="form-label fw-medium d-flex align-items-center gap-2 mb-2">
                                        <FileText size={18} />
                                        <span>Comentarios <span className="text-muted small fw-normal">(Opcional)</span></span>
                                    </label>
                                    <textarea
                                        id="comentarios"
                                        className="form-control"
                                        rows="4"
                                        placeholder="Describe brevemente el trabajo realizado..."
                                        value={comentarios}
                                        onChange={(e) => setComentarios(e.target.value)}
                                        maxLength={1000}
                                    />
                                    <small className="text-muted d-block mt-1">
                                        {comentarios.length}/1000 caracteres
                                    </small>
                                </div>

                                <div className="d-grid gap-2">
                                    <button type="submit" className="btn btn-primary" disabled={loading || success}>
                                        {loading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Registrando...
                                            </>
                                        ) : success ? (
                                            <>
                                                <CheckCircle size={18} className="me-2" />
                                                Registrado
                                            </>
                                        ) : (
                                            <>
                                                <Save size={18} className="me-2" />
                                                Registrar Horas
                                            </>
                                        )}
                                    </button>

                                    <Link to="/" className="btn btn-outline-secondary">
                                        Cancelar
                                    </Link>
                                </div>
                            </form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}